/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS } from '../programs';

export const ADD_COSIGNER_DISCRIMINATOR = new Uint8Array([
  17, 34, 183, 119, 202, 255, 218, 233,
]);

export function getAddCosignerDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    ADD_COSIGNER_DISCRIMINATOR,
  );
}

export type AddCosignerInstruction<
  TProgram extends string = typeof DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS,
  TAccountAdminAuthority extends string | AccountMeta<string> = string,
  TAccountConfig extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAdminAuthority extends string
        ? ReadonlySignerAccount<TAccountAdminAuthority> &
            AccountSignerMeta<TAccountAdminAuthority>
        : TAccountAdminAuthority,
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      ...TRemainingAccounts,
    ]
  >;

export type AddCosignerInstructionData = {
  discriminator: ReadonlyUint8Array;
  cosigner: Address;
};

export type AddCosignerInstructionDataArgs = { cosigner: Address };

export function getAddCosignerInstructionDataEncoder(): FixedSizeEncoder<AddCosignerInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['cosigner', getAddressEncoder()],
    ]),
    (value) => ({ ...value, discriminator: ADD_COSIGNER_DISCRIMINATOR }),
  );
}

export function getAddCosignerInstructionDataDecoder(): FixedSizeDecoder<AddCosignerInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['cosigner', getAddressDecoder()],
  ]);
}

export function getAddCosignerInstructionDataCodec(): FixedSizeCodec<
  AddCosignerInstructionDataArgs,
  AddCosignerInstructionData
> {
  return combineCodec(
    getAddCosignerInstructionDataEncoder(),
    getAddCosignerInstructionDataDecoder(),
  );
}

export type AddCosignerAsyncInput<
  TAccountAdminAuthority extends string = string,
  TAccountConfig extends string = string,
> = {
  adminAuthority: TransactionSigner<TAccountAdminAuthority>;
  config?: Address<TAccountConfig>;
  cosigner: AddCosignerInstructionDataArgs['cosigner'];
};

export async function getAddCosignerInstructionAsync<
  TAccountAdminAuthority extends string,
  TAccountConfig extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS,
>(
  input: AddCosignerAsyncInput<TAccountAdminAuthority, TAccountConfig>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  AddCosignerInstruction<
    TProgramAddress,
    TAccountAdminAuthority,
    TAccountConfig
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    adminAuthority: { value: input.adminAuthority ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            99, 111, 115, 105, 103, 110, 101, 114, 95, 104, 111, 111, 107, 95,
            99, 111, 110, 102, 105, 103,
          ]),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('adminAuthority', accounts.adminAuthority),
      getAccountMeta('config', accounts.config),
    ],
    data: getAddCosignerInstructionDataEncoder().encode(
      args as AddCosignerInstructionDataArgs,
    ),
    programAddress,
  } as AddCosignerInstruction<
    TProgramAddress,
    TAccountAdminAuthority,
    TAccountConfig
  >);
}

export type AddCosignerInput<
  TAccountAdminAuthority extends string = string,
  TAccountConfig extends string = string,
> = {
  adminAuthority: TransactionSigner<TAccountAdminAuthority>;
  config: Address<TAccountConfig>;
  cosigner: AddCosignerInstructionDataArgs['cosigner'];
};

export function getAddCosignerInstruction<
  TAccountAdminAuthority extends string,
  TAccountConfig extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS,
>(
  input: AddCosignerInput<TAccountAdminAuthority, TAccountConfig>,
  config?: { programAddress?: TProgramAddress },
): AddCosignerInstruction<
  TProgramAddress,
  TAccountAdminAuthority,
  TAccountConfig
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    adminAuthority: { value: input.adminAuthority ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('adminAuthority', accounts.adminAuthority),
      getAccountMeta('config', accounts.config),
    ],
    data: getAddCosignerInstructionDataEncoder().encode(
      args as AddCosignerInstructionDataArgs,
    ),
    programAddress,
  } as AddCosignerInstruction<
    TProgramAddress,
    TAccountAdminAuthority,
    TAccountConfig
  >);
}

export type ParsedAddCosignerInstruction<
  TProgram extends string = typeof DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    adminAuthority: TAccountMetas[0];
    config: TAccountMetas[1];
  };
  data: AddCosignerInstructionData;
};

export function parseAddCosignerInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedAddCosignerInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { adminAuthority: getNextAccount(), config: getNextAccount() },
    data: getAddCosignerInstructionDataDecoder().decode(instruction.data),
  };
}
